<script setup>
import { computed, ref, watch } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import { ClockIcon } from "@heroicons/vue/20/solid";
import { twMerge } from "tailwind-merge";

const props = defineProps({
    modelValue: {
        type: Object,
        default: () => ({
            hours: 0,
            minutes: 0,
        }),
    },
    teleport: {
        type: HTMLElement,
        default: () => null,
    },
    z: {
        type: Number,
        default: 0,
    },
    errors: {
        type: Array,
        default: [],
    },
});

const emit = defineEmits(["update:modelValue"]);

const time = ref(props.modelValue);
const open = ref(false);

const buttonClasses = computed(() => {
    let classes = [
        "inline-flex w-full rounded-md border-0 py-1.5 pl-3 pr-14 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset text-sm leading-6",
        "text-gray-900 bg-white ring-gray-300 focus:ring-gray-950 placeholder:text-gray-400 focus-visible:outline-none",
    ];

    switch (props.z) {
        case 3:
            classes.push("dark:text-white dark:bg-gray-600 dark:ring-gray-500 dark:focus:ring-gray-400 dark:placeholder:text-gray-300");
            break;
        case 2:
            classes.push("dark:text-white dark:bg-gray-700 dark:ring-gray-600 dark:focus:ring-gray-500 dark:placeholder:text-gray-400");
            break;
        case 1:
            classes.push("dark:text-white dark:bg-gray-800 dark:ring-gray-700 dark:focus:ring-gray-600 dark:placeholder:text-gray-400");
            break;
        default:
            classes.push("dark:text-white dark:bg-gray-900 dark:ring-gray-800 dark:focus:ring-gray-700 dark:placeholder:text-gray-400");
            break;
    }

    if (props.errors.length > 0) {
        classes.push("ring-red-500 focus:ring-red-500 dark:ring-red-500 dark:focus:ring-red-500");
    }

    return twMerge(classes);
});

const buttonIconClasses = computed(() => {
    let classes = ["w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-500"];

    switch (props.z) {
        case 3:
            classes.push("dark:text-gray-300 hover:dark:text-gray-100");
            break;
        case 2:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        case 1:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
        default:
            classes.push("dark:text-gray-400 hover:dark:text-gray-300");
            break;
    }

    return twMerge(classes);
});

watch(time, () => {
    emit("update:modelValue", time.value);
});
</script>

<template>
    <Datepicker @open="open = true" @closed="open = false" v-model="time" :hide-input-icon="true" :clearable="false" :dark="true" time-picker>
        <template #dp-input="{ value }">
            <button type="button" :class="buttonClasses" aria-expanded="true" aria-haspopup="true">
                <span class="block">{{ value }}</span>
                <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ClockIcon :class="buttonIconClasses" aria-hidden="true" />
                </span>
            </button>
        </template>
        <template #action-row="{ closePicker, selectDate }">
            <div class="w-full flex justify-between items-center">
                <button
                    type="button"
                    class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-400 bg-gray-900 rounded-md focus-visible:outline-2 focus-visible:outline-gray-600 focus-visible:outline focus-visible:outline-offset-2 hover:text-gray-200 hover:bg-gray-800 hover:shadow-sm"
                    @click="closePicker">
                    Cancel
                </button>
                <button
                    type="button"
                    class="inline-flex items-center gap-x-1.5 px-3 py-2 text-sm font-semibold text-gray-200 bg-gray-800 rounded-md shadow-sm focus-visible:outline-2 focus-visible:outline-gray-600 focus-visible:outline focus-visible:outline-offset-2 hover:bg-gray-700"
                    @click="selectDate">
                    Select
                </button>
            </div>
        </template>
    </Datepicker>
</template>
